/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { linkClick } from '../shell/router';
import { Icon } from './Icon';

export interface PageTab {
  id: string;
  label: string;
  href: string;
  count?: number;
}

/** What the shell knows about the page on screen: its place in the rail, its title and its sub-pages. */
export interface PageFrame {
  crumbs: string[];
  title: string;
  tabs?: PageTab[];
  activeTab?: string;
}

const PageFrameContext = createContext<PageFrame>({ crumbs: [], title: '' });
export const PageFrameProvider = PageFrameContext.Provider;
export function usePageFrame(): PageFrame {
  return useContext(PageFrameContext);
}

export interface PageHeaderProps {
  /** Defaults to the page's name in the rail. */
  title?: ReactNode;
  /** Defaults to the rail group and page. */
  crumbs?: string[];
  /** One line beside the title: when the data is from, or status chips. */
  meta?: ReactNode;
  /** Buttons on the right; at most one primary. */
  actions?: ReactNode;
  tabs?: PageTab[];
  activeTab?: string;
}

/**
 * The top of every page, sticky while the page scrolls: the breadcrumb (group › page), the title, one line of meta, the
 * actions on the right, and underline tabs when the page has sub-views. It publishes its own height as
 * `--page-header-h` so table headers stick just below it.
 */
export function PageHeader({ title, crumbs, meta, actions, tabs, activeTab }: PageHeaderProps) {
  const frame = usePageFrame();
  const shownCrumbs = crumbs ?? frame.crumbs;
  const shownTabs = tabs ?? frame.tabs;
  const shownActive = activeTab ?? frame.activeTab;
  const ref = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const header = ref.current;
    const scroller = header?.closest<HTMLElement>('[data-scroller]');
    if (!header || !scroller) return;
    const publish = () => scroller.style.setProperty('--page-header-h', `${header.offsetHeight}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    <header ref={ref} className={`ui-page-header${shownTabs?.length ? ' has-tabs' : ''}`}>
      <div className="ui-page-header-inner">
        {shownCrumbs.length > 0 ? (
          <nav aria-label="Breadcrumb">
            <ol className="ui-crumbs">
              {shownCrumbs.map((crumb, index) => (
                <li key={`${crumb}-${index}`} aria-current={index === shownCrumbs.length - 1 ? 'page' : undefined}>
                  {index > 0 ? <Icon icon={ChevronRight} size="xs" /> : null}
                  <span>{crumb}</span>
                </li>
              ))}
            </ol>
          </nav>
        ) : null}
        <div className="ui-title-row">
          <h1 className="ui-title">{title ?? frame.title}</h1>
          {meta ? <div className="ui-title-meta">{meta}</div> : null}
          {actions ? <div className="ui-title-actions">{actions}</div> : null}
        </div>
        {shownTabs && shownTabs.length > 0 ? (
          <nav className="ui-tabs" aria-label="Page views">
            {shownTabs.map((tab) => (
              <a
                key={tab.id}
                href={tab.href}
                className={`ui-tab${tab.id === shownActive ? ' is-active' : ''}`}
                aria-current={tab.id === shownActive ? 'page' : undefined}
                onClick={(event) => linkClick(event)}
              >
                {tab.label}
                {tab.count ? <span className="ui-count">{tab.count}</span> : null}
              </a>
            ))}
          </nav>
        ) : null}
      </div>
    </header>
  );
}
